// The profile (docs/SPEC.md "Profile sync"), as the rest of the site sees it. THE BOUNDARY between the map / feed /
// filters / card (slices 1–2) and the profile (slice 3): slices 1–2 only READ `profileStore`'s `view` (and `events`,
// the server's copies of starred / hidden events), through the helpers below. Slice 3 writes it, through
// `getProfile()` (stars, hide, type levels, I'm free) and the sync with the server (src/profile/sync.ts). These stay:
//   - `ProfileView` and its fields, with `view` replaced by a NEW object whenever anything in it changes (the
//     filtered set and the map compare it by identity, web/filters.js memo);
//   - `profileStore` (a Zustand vanilla store: getState / subscribe), `useProfileView()`, `isHiddenEvent()`,
//     `profileEvent()`.
// Before the first sync the view is what this device saved last time (stars and levels survive a reload).
import { createStore, useStore, type StoreApi } from "zustand";
import { useShallow } from "zustand/react/shallow";
import { SHARED } from "../shared/config";
import { type NowishEvent } from "../shared/rules";
import { freeUntil } from "../profile/free";
import { parseEvent, type RawEvent as RawEventFromServer } from "../data/event";
import { isHidden, normTag, profileView, type ProfileView as RawView, type TagLevel } from "../profile/model";
import { ProfileSync, type EventCopy, type SyncDeps } from "../profile/sync";

export type { TagLevel };

export interface ProfileView {
  /** tag (normalized: trimmed, lower case) → level. */
  readonly levels: ReadonlyMap<string, TagLevel>;
  /** Tags at "interest". */
  readonly interests: ReadonlySet<string>;
  /** Tags at "hidden". */
  readonly hiddenTags: ReadonlySet<string>;
  /** Event ids hidden with "Not interested". */
  readonly hiddenEvents: ReadonlySet<string>;
  /** Event ids starred ("I'm interested"). */
  readonly stars: ReadonlySet<string>;
}

/** I'm free: until when (ms), null = until turned off. Kept out of the view: it changes nothing on the map or in the lists. */
export interface FreeState { readonly on: boolean; readonly until: number | null }

export const EMPTY_PROFILE: ProfileView = {
  levels: new Map(), interests: new Set(), hiddenTags: new Set(), hiddenEvents: new Set(), stars: new Set(),
};
const NOT_FREE: FreeState = { on: false, until: null };

export interface ProfileState {
  readonly view: ProfileView;
  readonly free: FreeState;
  /** id → event: the server's copies of starred / hidden events (GET /profile `events`), parsed. */
  readonly events: ReadonlyMap<string, NowishEvent>;
  /** The server has answered at least once this session. */
  readonly loaded: boolean;
  /** Changes made here the server hasn't acknowledged yet (the offline queue). */
  readonly pending: number;
  /** Changes the server refused this session (shown as never made; the Profile sheet says so). */
  readonly rejected: number;
}

export const profileStore = createStore<ProfileState>()(() => ({ view: EMPTY_PROFILE, free: NOT_FREE, events: new Map(), loaded: false, pending: 0, rejected: 0 }));

export const useProfileView = () => useStore(profileStore, (s) => s.view);
export const useFree = () => useStore(profileStore, (s) => s.free);
/** Whether one event is starred: a selector, so a star button redraws for its own event only. */
export const starredSelector = (id: string) => (s: ProfileState) => s.view.stars.has(id);
export const useIsStarred = (id: string) => useStore(profileStore, starredSelector(id));
/** The server's copies of starred / hidden events (changes when they arrive or change). */
export const useProfileEvents = () => useStore(profileStore, (s) => s.events);
/** The server has answered this session / changes still waiting to go up (the Profile sheet's "synced" line). */
export const useProfileSync = () => useStore(profileStore, useShallow((s) => ({ loaded: s.loaded, pending: s.pending, rejected: s.rejected })));

/** "Not interested": hidden by you, or a hidden type with none of your interests (RULES.hides, shared/cases/hidden.json). */
export function isHiddenEvent(e: NowishEvent, v: ProfileView = profileStore.getState().view) {
  return isHidden(e, v as RawView);
}

/** A starred or hidden event the feed doesn't hold (outside the fetched area or window): the profile's copy. */
export const profileEvent = (id: string): NowishEvent | null => profileStore.getState().events.get(id) ?? null;

// ---------- writing it ----------

/** What the page can do to the profile. Everything shows at once and syncs in the background. */
export interface ProfileController {
  sync(): Promise<void>;
  /** Syncs now and then every minute while the page is visible, and when it becomes visible again. Returns a stop function. */
  start(): () => void;
  isStarred(id: string): boolean;
  tagLevel(tag: string): TagLevel | null;
  setStar(e: NowishEvent, on: boolean): void;
  toggleStar(e: NowishEvent): void;
  hideEvent(e: NowishEvent): void;
  unhideEvent(id: string): void;
  /** The one way to change how you feel about a type: one level per type (null clears it). */
  setTagLevel(tag: string, level: TagLevel | null): void;
  /** Replace the interests with nowish.json's starting list. */
  resetInterests(): void;
  /** I'm free for a length from nowish.json freeLengths (null = off). */
  setFree(lengthId: string | null): void;
  /** Which length was picked last (decides "until 3am" vs "2h 10m"); this device only. */
  freeLength(): string | null;
}

export type ProfileDeps = Omit<SyncDeps, "onChange">;

const FREE_LENGTH_KEY = "nowish-next-free-length";
const MAX_TIMER = 2 ** 31 - 1;

const sameSet = (a: ReadonlySet<string>, b: ReadonlySet<string>) => a.size === b.size && [...a].every((x) => b.has(x));
const sameView = (a: ProfileView, b: ProfileView) =>
  sameSet(a.stars, b.stars) && sameSet(a.hiddenEvents, b.hiddenEvents) && sameSet(a.interests, b.interests)
  && sameSet(a.hiddenTags, b.hiddenTags) && a.levels.size === b.levels.size && [...b.levels].every(([k, v]) => a.levels.get(k) === v);

/** Connects a store to the server sync and returns the actions. (The page uses `getProfile()`; tests bind their own store.) */
export function bindProfile(store: StoreApi<ProfileState>, deps: ProfileDeps): ProfileController {
  let freeTimer: unknown = null;
  let lastRawEvents = "{}";
  const refresh = () => {
    const now = deps.now();
    const raw = profileView(sync.data, now);
    const prev = store.getState();
    const { free, ...rest } = raw;
    // The same view object unless something the map and lists read changed (they compare it by identity).
    const view: ProfileView = sameView(prev.view, rest) ? prev.view : rest;
    // The copies are re-parsed when the server's changed in any way (a new venue, end or price), not otherwise.
    const rawEvents = JSON.stringify(sync.data.events);
    const events = rawEvents === lastRawEvents ? prev.events
      : new Map(Object.values(sync.data.events).map((r) => [r.id, parseEvent(r as unknown as RawEventFromServer)]));
    lastRawEvents = rawEvents;
    store.setState({
      view, events, loaded: sync.loaded, pending: sync.data.outbox.length, rejected: sync.rejected.length,
      free: free.on === prev.free.on && free.until === prev.free.until ? prev.free : free,
    });
    // I'm free ends by its own clock: draw it off the moment it does.
    deps.clearTimer(freeTimer);
    freeTimer = null;
    if (free.on && free.until != null) {
      freeTimer = deps.setTimer(refresh, Math.min(Math.max(free.until - now + 1, 1), MAX_TIMER));
    }
  };
  const sync = new ProfileSync({ ...deps, onChange: refresh });
  refresh();

  const readLength = (): string | null => { try { return deps.storage?.getItem(FREE_LENGTH_KEY) ?? null; } catch { return null; } };
  const level = (tag: string): TagLevel | null => store.getState().view.levels.get(normTag(tag)) ?? null;
  const starred = (id: string) => store.getState().view.stars.has(id);
  const copy = (e: NowishEvent): EventCopy => e as unknown as EventCopy;

  const c: ProfileController = {
    sync: () => sync.sync(),
    start() {
      void sync.sync();
      const timer = setInterval(() => { if (document.visibilityState === "visible") void sync.sync(); }, 60e3);
      const visible = () => { if (document.visibilityState === "visible") void sync.sync(); };
      document.addEventListener("visibilitychange", visible);
      // Another tab saved its queue: take in what it queued, so neither tab's save wipes the other's.
      const stored = (ev: StorageEvent) => { if (ev.key === null || ev.key.startsWith("nowish-next-profile")) sync.adoptStored(); };
      window.addEventListener("storage", stored);
      return () => { clearInterval(timer); document.removeEventListener("visibilitychange", visible); window.removeEventListener("storage", stored); };
    },
    isStarred: starred,
    tagLevel: level,
    setStar: (e, on) => sync.set("stars", e.id, on, { event: copy(e) }),
    toggleStar: (e) => c.setStar(e, !starred(e.id)),
    hideEvent: (e) => sync.set("hidden-events", e.id, true, { event: copy(e) }),
    unhideEvent: (id) => sync.set("hidden-events", id, false),
    setTagLevel(tag, next) {
      if (level(tag) === next) return;
      sync.set("tag-levels", tag, next != null, { value: next });
    },
    resetInterests() {
      const mine = [...store.getState().view.interests];
      for (const t of mine) if (!SHARED.seedInterests.includes(t)) c.setTagLevel(t, null);
      for (const t of SHARED.seedInterests) c.setTagLevel(t, "interest");
    },
    setFree(lengthId) {
      if (!lengthId) { sync.set("free", "free", false); return; }
      const until = freeUntil(lengthId, new Date(deps.now()));
      try { deps.storage?.setItem(FREE_LENGTH_KEY, lengthId); } catch { /* refused */ }
      sync.set("free", "free", true, { value: until == null ? null : String(until) });
    },
    freeLength: readLength,
  };
  return c;
}

let controller: ProfileController | null = null;
/** The page's profile: bound to `profileStore` and the real server the first time it's asked for. */
export function getProfile(): ProfileController {
  controller ??= bindProfile(profileStore, {
    fetch: (...a) => fetch(...a),
    storage: (() => { try { return localStorage; } catch { return null; } })(),
    profileId: new URLSearchParams(location.search).get("profile"),
    now: () => Date.now(),
    setTimer: (fn, ms) => setTimeout(fn, ms),
    clearTimer: (t) => clearTimeout(t as number),
  });
  // A handle for the browser tests (like nowishDebug.map): the same actions the buttons call.
  const w = window as unknown as { nowishDebug?: Record<string, unknown> };
  w.nowishDebug = { ...w.nowishDebug, profile: controller };
  return controller;
}

